import React from 'react';
import PropTypes from 'prop-types';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';
import { processAvailabilityForDay, TimeSlot } from '../../utils/restaurant-availability.js';
import { Modal } from '../../components/ui/Modal';

// Component for displaying a single day's schedule
const DaySchedule = ({ dayLabel, isAvailable, timeSlots }) => {
  const { language } = useDataContext();

  if (!isAvailable) {
    return (
      <div className="py-2">
        <span className="font-medium text-gray-700 inline-block w-24">{dayLabel}:</span>
        <span className="text-gray-600">{translations[language]?.restaurant_closed}</span>
      </div>
    );
  }

  return (
    <div className="py-2 flex">
      {/* Day label column */}
      <div className="w-24">
        <span className="font-medium text-gray-700">{dayLabel}:</span>
      </div>

      {/* All time slots column */}
      <div className="flex-1">
        {timeSlots.map((slot, index) => (
          <React.Fragment key={`${dayLabel}-slot-${index}`}>
            {index > 0 && <br />}
            <TimeSlot start={slot.start} end={slot.end} />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

DaySchedule.propTypes = {
  dayLabel: PropTypes.string.isRequired,
  isAvailable: PropTypes.bool.isRequired,
  timeSlots: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string.isRequired,
      end: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

// Main availability component
const RestaurantAvailabilityModal = ({ isOpen, onClose, availability, className, daysConfig }) => {
  const { language } = useDataContext();

  // Default days configuration
  const days = daysConfig || [
    { key: 'Mon', label: translations[language]?.monday },
    { key: 'Tue', label: translations[language]?.tuesday },
    { key: 'Wed', label: translations[language]?.wednesday },
    { key: 'Thu', label: translations[language]?.thursday },
    { key: 'Fri', label: translations[language]?.friday },
    { key: 'Sat', label: translations[language]?.saturday },
    { key: 'Sun', label: translations[language]?.sunday },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations[language]?.restaurant_menu_opening_hours_title}
      contentClassName="max-w-[260px]"
    >
      <div className={`flex flex-col space-y-1 ${className || ''}`}>
        {days.map((day) => {
          const dayData = processAvailabilityForDay(availability, days, day.key, day.label);

          return (
            <DaySchedule
              key={day.key}
              dayLabel={dayData.dayLabel}
              isAvailable={dayData.isAvailable}
              timeSlots={dayData.timeSlots}
            />
          );
        })}
      </div>
    </Modal>
  );
};

RestaurantAvailabilityModal.propTypes = {
  availability: PropTypes.object.isRequired,
  className: PropTypes.string,
  daysConfig: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

export default RestaurantAvailabilityModal;
